import { Outlet, useLocation } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { LoadingState } from '../components/ui/LoadingState';
import { useAppContext } from '../context/AppContext';
import { useVerification } from '../context/useVerification';
import { createQuoteTrackingContext } from '../lib/quoteTracking';
import { Suspense, lazy, useEffect, useState } from 'react';
import { getConsentState } from '../lib/consentState';

type IdleWindow = Window &
    typeof globalThis & {
        requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
        cancelIdleCallback?: (handle: number) => void;
    };

const loadQuoteModal = () =>
    import('../components/features/QuoteModal').then((module) => ({ default: module.QuoteModal }));

const QuoteModal = lazy(loadQuoteModal);
const CookieBanner = lazy(() =>
    import('../components/features/CookieBanner').then((module) => ({ default: module.CookieBanner }))
);

export const MainLayout = () => {
    const {
        activeLang,
        handleLangChange,
        openQuoteModal,
        closeQuoteModal,
        t,
        isLoading,
        isQuoteModalOpen,
        quoteInitialData,
        quoteTrackingContext,
        onInitialDataProcessed,
        files,
        handleFilesAdded,
        removeFile,
        removeFiles,
        clearFiles,
        updateFileLanguage,
        toggleFileService,
        updateFileTier,
        updateFileSpecialism,
        uploadError,
        services,
        tiers,
        specialisms,
        availableLanguages,
        updateFileOCRStatus,
        updateFileAnalysisResult,
        updateFileStorageReference,
        updateFileClassificationResult,
        settings,
        deliveryOptions,
        quoteFlowConfig,
    } = useAppContext();
    
    const { prepareQuoteVerification } = useVerification();
    const location = useLocation();
    const isOrderStatusRoute = /\/order-status$/.test(location.pathname);
    const [cookiePreferencesOpen, setCookiePreferencesOpen] = useState(false);
    const [shouldMountCookieBanner, setShouldMountCookieBanner] = useState(() => !getConsentState().decided);

    const handleOpenQuote = (options?: Parameters<typeof openQuoteModal>[0]) => {
        prepareQuoteVerification();
        if (typeof options !== 'undefined') {
            openQuoteModal(options);
            return;
        }
        openQuoteModal({
            tracking: createQuoteTrackingContext({
                entrypoint: 'main_layout_quote',
                surface: 'layout',
                isCta: false,
            }),
        });
    };

    useEffect(() => {
        void import('../lib/analytics').then(({ initAnalytics }) => {
            initAnalytics();
        });
    }, []);

    useEffect(() => {
        if (!activeLang?.code) return;
        void import('../lib/analytics').then(({ trackPageView }) => {
            trackPageView(location.pathname, activeLang.code);
        });
    }, [activeLang?.code, location.pathname]);

    useEffect(() => {
        if (cookiePreferencesOpen) {
            setShouldMountCookieBanner(true);
        }
    }, [cookiePreferencesOpen]);

    useEffect(() => {
        if (!t) return;

        const preload = () => {
            void loadQuoteModal();
        };

        const idleWindow = window as IdleWindow;

        if (typeof idleWindow.requestIdleCallback === 'function' && typeof idleWindow.cancelIdleCallback === 'function') {
            const idleId = idleWindow.requestIdleCallback(preload, { timeout: 1500 });
            return () => idleWindow.cancelIdleCallback?.(idleId);
        }

        const timeoutId = globalThis.setTimeout(preload, 400);
        return () => globalThis.clearTimeout(timeoutId);
    }, [t]);

    if (!t && isLoading && isOrderStatusRoute) {
        return (
            <div className="min-h-screen bg-white">
                <main className="pt-2 md:pt-3">
                    <Outlet />
                </main>
            </div>
        );
    }

    if (!t && isLoading && !isOrderStatusRoute) {
        return (
            <div className="min-h-screen w-full bg-[#f0f0ed] px-4">
                <LoadingState
                    mode="page"
                    size="lg"
                    label="Loading language..."
                    className="mx-auto max-w-sm"
                />
            </div>
        );
    }

    return (
        <div className="min-h-screen overflow-x-hidden bg-white font-sans text-stone-800 selection:bg-green-100 selection:text-green-900">
            <Header
                activeLang={activeLang}
                handleLangChange={handleLangChange}
                openQuoteModal={(options) => void handleOpenQuote(options)}
                t={t}
                isLoading={isLoading}
            />

            <main className={isLoading ? 'pt-2 md:pt-3 opacity-50 transition-opacity duration-300' : 'pt-2 md:pt-3 opacity-100 transition-opacity duration-300'}>
                <Outlet />
            </main>

            <Footer t={t} onOpenCookiePreferences={() => setCookiePreferencesOpen(true)} />
            {shouldMountCookieBanner ? (
                <Suspense fallback={null}>
                    <CookieBanner
                        t={t}
                        preferencesOpen={cookiePreferencesOpen}
                        onOpenPreferences={() => setCookiePreferencesOpen(true)}
                        onClosePreferences={() => setCookiePreferencesOpen(false)}
                    />
                </Suspense>
            ) : null}

            {isQuoteModalOpen ? (
                <Suspense
                    fallback={
                        <div className="fixed inset-0 z-50 bg-stone-950/20 backdrop-blur-[2px]" aria-hidden="true" />
                    }
                >
                    <QuoteModal
                        isOpen={isQuoteModalOpen}
                        onClose={closeQuoteModal}
                        t={t}
                        activeLang={activeLang}
                        initialData={quoteInitialData}
                        onInitialDataProcessed={onInitialDataProcessed}
                        files={files}
                        handleFilesAdded={(f) => void handleFilesAdded(f)}
                        removeFile={removeFile}
                        removeFiles={removeFiles}
                        clearFiles={clearFiles}
                        updateFileLanguage={updateFileLanguage}
                        toggleFileService={toggleFileService}
                        updateFileTier={updateFileTier}
                        updateFileSpecialism={updateFileSpecialism}
                        uploadError={uploadError}
                        services={services}
                        tiers={tiers}
                        availableLanguages={availableLanguages}
                        updateFileOCRStatus={updateFileOCRStatus}
                        updateFileAnalysisResult={updateFileAnalysisResult}
                        updateFileStorageReference={updateFileStorageReference}
                        updateFileClassificationResult={updateFileClassificationResult}
                        settings={settings}
                        deliveryOptions={deliveryOptions}
                        quoteFlowConfig={quoteFlowConfig}
                        specialisms={specialisms}
                        trackingContext={quoteTrackingContext}
                    />
                </Suspense>
            ) : null}
        </div>
    );
};
